import React, { useState, useEffect, useImperativeHandle } from 'react';
import {
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  Row,
  Col,
  Card
} from 'antd';
import dayjs from 'dayjs';
import AssetSearchSelect from './AssetSearchSelect';

const { Option } = Select;
const { TextArea } = Input;

/**
 * 资产动态表单组件
 * 根据资产类型动态渲染对应的字段配置
 */
const AssetDynamicForm = React.forwardRef(({ 
  assetTypeId, 
  form, 
  initialValues = {},
  mode = 'create'
}, ref) => {
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (assetTypeId) {
      fetchFieldConfigs();
    }
  }, [assetTypeId]);

  // 获取字段配置
  const fetchFieldConfigs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/assets/types/${assetTypeId}/fields`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFieldConfigs(data.data);
        
        // 如果是编辑模式，设置初始值
        if (mode === 'edit' && Object.keys(initialValues).length > 0) {
          const formValues = {};
          
          // 处理动态字段的初始值
          data.data.forEach(field => {
            const fieldKey = `field_${field.field_code}`;
            if (initialValues[fieldKey] !== undefined) {
              if (field.field_type === 'date') {
                formValues[fieldKey] = initialValues[fieldKey] ? dayjs(initialValues[fieldKey]) : null;
              } else if (field.field_type === 'boolean') {
                formValues[fieldKey] = Boolean(initialValues[fieldKey]);
              } else {
                formValues[fieldKey] = initialValues[fieldKey];
              }
            } else if (field.default_value !== null) {
              formValues[fieldKey] = field.default_value;
            }
          });
          
          form.setFieldsValue(formValues);
        }
      }
    } catch (error) {
      console.error('获取字段配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 渲染单个字段
  const renderField = (field) => {
    const fieldKey = `field_${field.field_code}`;
    const fieldProps = {
      name: fieldKey,
      label: field.field_name,
      required: field.is_required,
      extra: field.validation_rules ? field.validation_rules.description : undefined,
      initialValue: field.default_value || undefined
    };


    // 添加验证规则
    const rules = [];
    if (field.is_required) {
      rules.push({ required: true, message: `请输入${field.field_name}` });
    }
    
    if (field.validation_rules) {
      if (field.validation_rules.min_length) {
        rules.push({ min: field.validation_rules.min_length, message: `最少${field.validation_rules.min_length}个字符` });
      }
      if (field.validation_rules.max_length) {
        rules.push({ max: field.validation_rules.max_length, message: `最多${field.validation_rules.max_length}个字符` });
      }
      if (field.validation_rules.pattern) {
        rules.push({ pattern: new RegExp(field.validation_rules.pattern), message: field.validation_rules.pattern_message || '格式不正确' });
      }
    }
    
    if (rules.length > 0) {
      fieldProps.rules = rules;
    }

    switch (field.field_type) {
      case 'text':
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder={`请输入${field.field_name}`} />
          </Form.Item>
        );

      case 'textarea':
        return (
          <Form.Item {...fieldProps}>
            <TextArea 
              rows={3} 
              placeholder={`请输入${field.field_name}`}
            />
          </Form.Item>
        );

      case 'number':
        return (
          <Form.Item {...fieldProps}>
            <InputNumber
              style={{ width: '100%' }}
              placeholder={`请输入${field.field_name}`}
              precision={field.field_code.includes('price') || field.field_code.includes('fee') ? 2 : 0}
              min={0}
            />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item {...fieldProps}>
            <DatePicker 
              style={{ width: '100%' }}
              placeholder={`请选择${field.field_name}`}
            />
          </Form.Item>
        );

      case 'select':
        // 解析字段选项
        const parseFieldOptions = (fieldOptions) => {
          if (!fieldOptions) return [];
          
          try {
            // 如果是字符串，先解析
            let parsedOptions = fieldOptions;
            if (typeof fieldOptions === 'string') {
              parsedOptions = JSON.parse(fieldOptions);
            }
            
            if (Array.isArray(parsedOptions)) {
              // 如果是数组格式，直接使用
              return parsedOptions.map((option, index) => ({
                key: index,
                value: option,
                label: option
              }));
            } else if (typeof parsedOptions === 'object' && parsedOptions.options) {
              // 如果是对象格式，使用options数组
              return parsedOptions.options.map((option, index) => ({
                key: index,
                value: option.value || option,
                label: option.label || option.value || option
              }));
            }
          } catch (error) {
            console.error('解析字段选项失败:', error);
          }
          
          return [];
        };
        
        const selectOptions = parseFieldOptions(field.field_options);
        
        return (
          <Form.Item {...fieldProps}>
            <Select placeholder={`请选择${field.field_name}`}>
              {selectOptions.map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'boolean':
        return (
          <Form.Item {...fieldProps} valuePropName="checked">
            <Switch />
          </Form.Item>
        );

      case 'email':
        return (
          <Form.Item {...fieldProps}>
            <Input 
              type="email" 
              placeholder={`请输入${field.field_name}`}
            />
          </Form.Item>
        );

      case 'phone':
        return (
          <Form.Item {...fieldProps}>
            <Input 
              placeholder={`请输入${field.field_name}`}
              maxLength={11}
            />
          </Form.Item>
        );

      case 'asset_reference':
        // 解析关联的资产类型ID
        const getReferencedAssetTypeId = (fieldOptions) => {
          if (!fieldOptions) return null;
          
          try {
            let parsedOptions = fieldOptions;
            if (typeof fieldOptions === 'string') {
              parsedOptions = JSON.parse(fieldOptions);
            }
            
            if (typeof parsedOptions === 'object' && parsedOptions.referenced_asset_type_id) {
              return parsedOptions.referenced_asset_type_id;
            }
          } catch (error) {
            console.error('解析资产关联配置失败:', error);
          }
          
          return null;
        };
        
        const referencedAssetTypeId = getReferencedAssetTypeId(field.field_options);
        
        return (
          <Form.Item {...fieldProps}>
            <AssetSearchSelect
              assetTypeId={referencedAssetTypeId}
              placeholder={`请选择${field.field_name}`}
            />
          </Form.Item>
        );

      default:
        return (
          <Form.Item {...fieldProps}>
            <Input placeholder={`请输入${field.field_name}`} />
          </Form.Item>
        );
    }
  };

  // 处理表单提交时的数据转换
  const processFormData = (values) => {
    const fields = [];
    
    Object.keys(values).forEach(key => {
      if (key.startsWith('field_')) {
        const fieldCode = key.replace('field_', '');
        const fieldConfig = fieldConfigs.find(f => f.field_code === fieldCode);
        
        if (fieldConfig) {
          let fieldValue = values[key];
          
          // 处理特殊字段类型的数据转换
          if (fieldConfig.field_type === 'date' && fieldValue) {
            fieldValue = dayjs(fieldValue).format('YYYY-MM-DD');
          } else if (fieldConfig.field_type === 'boolean') {
            fieldValue = fieldValue ? '1' : '0';
          } else if (fieldValue === null || fieldValue === undefined) {
            fieldValue = '';
          }
          
          fields.push({
            field_config_id: fieldConfig.id,
            field_value: String(fieldValue) // 确保转换为字符串
          });
        }
      }
    });
    
    return fields;
  };

  // 暴露处理方法给父组件
  useImperativeHandle(ref, () => ({
    processFormData
  }));

  if (loading) {
    return <div>加载字段配置中...</div>;
  }

  if (fieldConfigs.length === 0) {
    return null;
  }

  return (
    <Card title="详细信息" size="small">
      <Row gutter={16}>
        {fieldConfigs.map((field, index) => (
          <Col 
            key={field.id} 
            span={field.field_type === 'textarea' ? 24 : 12}
            style={{ marginBottom: 16 }}
          >
            {renderField(field)}
          </Col>
        ))}
      </Row>
    </Card>
  );
});

export default AssetDynamicForm;